import { useEffect, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { TextInputField } from "@/components/common/TextInputField";
import {
	cancelDeviceLicense,
	getDeviceLicenses,
	grantDeviceLicenses,
	withdrawDeviceLicenseCancel,
	type DeviceLicense,
	type DeviceLicenseList,
} from "@/lib/api";
import {
	deviceLicenseStatus,
	formatCredits,
	formatJstDate,
	formatJstMonthDay,
} from "@/lib/billing";
import { formatDateTime } from "@/lib/format-datetime";

/** 1 回に付与できる台数の上限。バックエンドの MaxDeviceLicenseGrantCount と揃える。 */
const MAX_GRANT_COUNT = 100;

/**
 * 「付与と履歴」のデバイスのライセンスのタブ。付与は組織の残高から台数 × 単価を引き (クレジットは付与しない)、
 * 成功したら onGranted で親に残高を取り直させる。解約は次の更新日に終わる予約で、更新日の前なら取り消せる。
 */
export function DeviceLicensesPanel({
	organizationId,
	onGranted,
}: {
	organizationId: string;
	onGranted: () => void;
}) {
	const [list, setList] = useState<DeviceLicenseList | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [count, setCount] = useState("1");
	const [reason, setReason] = useState("");
	const [invoiceNumber, setInvoiceNumber] = useState("");
	const [countError, setCountError] = useState<string | null>(null);
	const [reasonError, setReasonError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [granting, setGranting] = useState(false);
	const [cancelTarget, setCancelTarget] = useState<DeviceLicense | null>(null);
	const [rowError, setRowError] = useState<string | null>(null);
	const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		getDeviceLicenses(organizationId)
			.then((data) => {
				if (cancelled) return;
				setList(data);
				setLoadError(null);
			})
			.catch((err: Error) => {
				if (!cancelled) setLoadError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, [organizationId, refreshKey]);

	const reload = () => setRefreshKey((k) => k + 1);
	const listError = loadError ?? rowError;

	function validate(): number | null {
		let ok = true;
		const trimmed = count.trim();
		const parsed = Number(trimmed);
		if (!/^\d+$/.test(trimmed) || parsed < 1 || parsed > MAX_GRANT_COUNT) {
			setCountError(`台数は 1〜${MAX_GRANT_COUNT} で指定してください。`);
			ok = false;
		} else {
			setCountError(null);
		}
		if (!reason.trim()) {
			setReasonError("理由は必須です。");
			ok = false;
		} else {
			setReasonError(null);
		}
		return ok ? parsed : null;
	}

	async function handleGrant(e: React.FormEvent) {
		e.preventDefault();
		const parsed = validate();
		if (parsed === null) return;
		setSubmitError(null);
		setGranting(true);
		try {
			await grantDeviceLicenses({
				organizationId,
				count: parsed,
				reason: reason.trim(),
				invoiceNumber: invoiceNumber.trim() || undefined,
			});
			setCount("1");
			setReason("");
			setInvoiceNumber("");
			reload();
			onGranted();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setGranting(false);
		}
	}

	async function handleWithdraw(license: DeviceLicense) {
		setRowError(null);
		setWithdrawingId(license.id);
		try {
			await withdrawDeviceLicenseCancel(license.id);
			reload();
		} catch (err) {
			setRowError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setWithdrawingId(null);
		}
	}

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "row-reverse",
				alignItems: "stretch",
				flex: 1,
				minHeight: 0,
			}}
		>
			<div
				style={{
					flex: 1,
					minWidth: 0,
					padding: "16px 24px",
					borderLeft: "var(--pane-header-border)",
					overflowY: "auto",
				}}
			>
				<h4 style={sectionHeadingStyle}>ライセンス</h4>
				{listError !== null && <ErrorBox testId="device-license-list-error" message={listError} />}
				{list === null && loadError === null && (
					<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
						読み込み中…
					</p>
				)}
				{list !== null && list.licenses.length === 0 && (
					<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
						ライセンスはまだありません
					</p>
				)}
				{/* 付与日時・デバイス・状態・次の更新日・操作の 5 列。各行は subgrid で列幅を共有し、理由があれば 2 行目に出す。 */}
				{list !== null && list.licenses.length > 0 && (
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "auto minmax(0, 1fr) minmax(0, 1fr) auto auto",
							columnGap: 12,
							rowGap: 8,
							alignItems: "center",
							fontSize: "var(--text-caption)",
						}}
					>
						{list.licenses.map((license) => (
							<LicenseRow
								key={license.id}
								license={license}
								withdrawing={withdrawingId === license.id}
								onCancel={() => {
									setRowError(null);
									setCancelTarget(license);
								}}
								onWithdraw={() => void handleWithdraw(license)}
							/>
						))}
					</div>
				)}
			</div>

			<form
				onSubmit={handleGrant}
				noValidate
				data-testid="device-license-grant-form"
				style={{ width: 320, flexShrink: 0, overflowY: "auto" }}
			>
				<div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					{submitError && <ErrorBox testId="device-license-submit-error" message={submitError} />}
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-caption)",
							lineHeight: "var(--leading-normal)",
							color: "var(--fg-3)",
						}}
					>
						{list !== null && (
							<>
								1 台あたり {formatCredits(list.unitPriceCredits)}{" "}
								クレジットを、付与した日に残高から引きます。
							</>
						)}
						以後は毎月の応当日 (日本時間) に引いて更新します。残高が足りなければ付与できません。
					</p>
					<TextInputField
						label="台数"
						value={count}
						onChange={setCount}
						autoComplete="off"
						error={countError ?? undefined}
					/>
					<TextInputField
						label="理由"
						value={reason}
						onChange={setReason}
						autoComplete="off"
						error={reasonError ?? undefined}
					/>
					<TextInputField
						label="請求書番号 (任意)"
						value={invoiceNumber}
						onChange={setInvoiceNumber}
						autoComplete="off"
					/>
					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<ConfirmButton
							type="submit"
							label="ライセンスを付与"
							loading={granting}
							data-testid="device-license-grant"
						/>
					</div>
				</div>
			</form>

			<ConfirmDialog
				open={cancelTarget !== null}
				title="ライセンスを解約"
				message={cancelTarget === null ? "" : cancelMessage(cancelTarget)}
				confirmLabel="解約"
				onConfirm={async () => {
					if (cancelTarget === null) return;
					await cancelDeviceLicense(cancelTarget.id);
					setCancelTarget(null);
					reload();
				}}
				onCancel={() => setCancelTarget(null)}
			/>
		</div>
	);
}

function cancelMessage(license: DeviceLicense): string {
	const renewsOn = formatJstMonthDay(license.renewsAt);
	return license.deviceName === null
		? `このライセンスは更新日 (${renewsOn}) に終わります。`
		: `更新日 (${renewsOn}) にデバイス「${license.deviceName}」の登録を解除し、環境を止めます。`;
}

function LicenseRow({
	license,
	withdrawing,
	onCancel,
	onWithdraw,
}: {
	license: DeviceLicense;
	withdrawing: boolean;
	onCancel: () => void;
	onWithdraw: () => void;
}) {
	const status = deviceLicenseStatus(license);
	return (
		<div
			data-testid={`device-license-row-${license.id}`}
			style={{
				display: "grid",
				gridColumn: "1 / -1",
				gridTemplateColumns: "subgrid",
				rowGap: 2,
				alignItems: "center",
			}}
		>
			<span style={{ color: "var(--fg-3)" }}>{formatDateTime(license.grantedAt)}</span>
			<span
				data-testid={`device-license-device-${license.id}`}
				title={license.deviceName ?? undefined}
				data-tooltip-if-truncated
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					color: license.deviceName === null ? "var(--fg-3)" : "var(--fg-1)",
				}}
			>
				{status === "ended" ? "" : (license.deviceName ?? "空き")}
			</span>
			<span
				data-testid={`device-license-status-${license.id}`}
				style={{ overflowWrap: "anywhere" }}
			>
				<StatusText license={license} />
			</span>
			<span style={{ color: "var(--fg-2)", fontVariantNumeric: "tabular-nums" }}>
				{status === "ended" ? "" : `次の更新日 ${formatJstDate(license.renewsAt)}`}
			</span>
			<span style={{ display: "flex", justifyContent: "flex-end" }}>
				{status === "cancelScheduled" && (
					<ConfirmButton
						label="解約を取り消す"
						loading={withdrawing}
						size="sm"
						variant="secondary"
						onClick={onWithdraw}
						data-testid={`device-license-withdraw-${license.id}`}
					/>
				)}
				{(status === "active" || status === "unpaid") && (
					<ConfirmButton
						label="解約"
						loading={false}
						size="sm"
						variant="secondary"
						onClick={onCancel}
						data-testid={`device-license-cancel-${license.id}`}
					/>
				)}
			</span>
			{license.reason !== "" && (
				<span
					data-testid={`device-license-reason-${license.id}`}
					style={{ gridColumn: "2 / -1", color: "var(--fg-2)", overflowWrap: "anywhere" }}
				>
					{license.reason}
					{license.invoiceNumber !== null && (
						<span style={{ color: "var(--fg-3)" }}> ({license.invoiceNumber})</span>
					)}
				</span>
			)}
		</div>
	);
}

function StatusText({ license }: { license: DeviceLicense }) {
	const unpaid =
		license.unpaidSince === null ? null : (
			<span style={{ color: "var(--danger)" }}>
				支払い待ち ({formatJstMonthDay(license.unpaidSince)} から)
			</span>
		);
	switch (deviceLicenseStatus(license)) {
		case "ended":
			return (
				<span style={{ color: "var(--fg-3)" }}>終了 ({formatJstMonthDay(license.renewsAt)})</span>
			);
		case "cancelScheduled":
			return (
				<>
					<span style={{ color: "var(--warning)" }}>
						解約予定 ({formatJstMonthDay(license.renewsAt)} に終了)
					</span>
					{unpaid && <> · {unpaid}</>}
				</>
			);
		case "unpaid":
			return unpaid;
		case "active":
			return <span style={{ color: "var(--fg-1)" }}>有効</span>;
	}
}

function ErrorBox({ testId, message }: { testId: string; message: string }) {
	return (
		<div
			data-testid={testId}
			style={{
				padding: "10px 14px",
				marginBottom: 8,
				borderRadius: "var(--radius-3)",
				background: "var(--danger-subtle)",
				boxShadow: "0 0 0 1px var(--danger-line)",
				fontSize: "var(--text-ui)",
				color: "var(--danger)",
				fontFamily: "var(--font-sans)",
			}}
		>
			{message}
		</div>
	);
}

const sectionHeadingStyle: React.CSSProperties = {
	margin: "0 0 8px",
	fontSize: "var(--text-caption)",
	fontWeight: 600,
	letterSpacing: "var(--tracking-heading)",
	color: "var(--fg-2)",
};
